import { RawDraftContentState } from '@ms/yammer-content-support';
import {
  MessageSenderBadge,
  PraiseBadge,
  StorylineAnnouncementAudience,
  StorylineAnnouncementNotificationTarget,
  ThreadMessageLevel,
} from '@ms/yammer-graphql';
import type { ReactionType } from '@ms/yammer-graphql';

import {
  ActorReference,
  AnnouncementNotificationTargetEntityReference,
  AttachmentReference,
  Entity,
  EntityEdge,
  EntityMap,
  Group,
  GroupReference,
  MessageCampaignReference,
  Network,
  NetworkReference,
  SharedMessage,
  Thread,
  TopicReference,
  User,
  UserReference,
} from '../types';

export type MessageType = 'normal' | 'system' | 'poll' | 'praise';

export type MessageSubtype =
  | 'created_group'
  | 'created_network'
  | 'network_welcome'
  | 'added_participant'
  | 'removed_participant'
  | 'pm_to_group'
  | 'group_to_group'
  | 'local_network_policy_changed'
  | 'foreign_network_policy_changed'
  | 'poll_closed'
  | 'poll_reopened'
  | 'thread_closed'
  | 'thread_reopened'
  | 'changed_to_question'
  | 'changed_to_message'
  | 'export_complete';

export type MessageReference =
  | MessageCampaignReference
  | GroupReference
  | UserReference
  | TopicReference
  | NetworkReference;

export type CommonMessageFields =
  | 'id'
  | 'telemetryId'
  | 'threadId'
  | 'sender'
  | 'reactionsTotalCount'
  | 'reactionsCounts'
  | 'content';

export type CommonMessage = Pick<Message, CommonMessageFields>;

export type AttachmentsType = AttachmentReference['schema'] | 'mixed' | null;

export interface BaseMessageContent {
  readonly type: MessageType;
}

export interface ContentStateMessage extends BaseMessageContent {
  readonly body: RawDraftContentState;
  readonly language?: string;
}

export interface NormalMessageContent extends ContentStateMessage {
  readonly type: 'normal';
  readonly title?: string;
  readonly references?: MessageReference[];
}

export interface PollMessageContent extends ContentStateMessage {
  readonly type: 'poll';
  readonly answers: PollAnswer[];
  readonly isPollClosed: boolean;
  readonly viewerCanClosePoll: boolean;
}

export interface PollAnswer {
  readonly text: string;
  readonly index: number;
  readonly isSelected: boolean;
  readonly voteCount: number;
}

export interface PraiseMessageContent extends ContentStateMessage {
  readonly badge: PraiseBadge;
  readonly type: 'praise';
  readonly users: User['id'][];
}

export interface SystemMessageContent extends ContentStateMessage {
  readonly type: 'system';
  readonly references: MessageReference[];
}

export interface AddedParticipantMessageContent extends SystemMessageContent {
  readonly addedParticipantIds: User['id'][];
}

export interface CreatedGroupMessageContent extends SystemMessageContent {
  readonly createdGroupId: Group['id'];
}

export interface RemovedParticipantMessageContent extends SystemMessageContent {
  readonly removedParticipantIds: User['id'][];
}

export interface MovedGroupThreadToGroupMessageContent extends SystemMessageContent {
  readonly viewerHasAccessToSourceGroup: boolean;
  readonly sourceGroupId?: Group['id'];
  readonly viewerHasAccessToDestinationGroup: boolean;
  readonly destinationGroupId?: Group['id'];
}

export interface MovedDirectMessageToGroupMessageContent extends SystemMessageContent {
  readonly destinationGroupId: Group['id'];
}

export interface JoinedNetworkSystemMessageContent extends SystemMessageContent {
  readonly joinedNetworkId: Network['id'];
  readonly joinedUserId: User['id'];
}

export interface DisabledForeignNetworksMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface RemovedForeignNetworkSystemMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface ClosedThreadSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface ReopenedThreadSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface ClosedPollVotingSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface ReopenedPollVotingSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface CreatedNetworkSystemMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface MarkedMessageAsQuestionSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface UnmarkedMessageAsQuestionSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export const isNormalMessageContent = (content: Message['content']): content is NormalMessageContent =>
  content.type === 'normal';

export const isSystemMessageContent = (content: Message['content']): content is SystemMessageContent =>
  content.type === 'system';

export const isPollMessageContent = (content: Message['content']): content is PollMessageContent =>
  content.type === 'poll';

export const isPraiseMessageContent = (content: Message['content']): content is PraiseMessageContent =>
  content.type === 'praise';

export const isAddedParticipantsContent = (
  content: Message['content'],
  message: Message
): content is AddedParticipantMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'added_participant';
};

export const isCreatedGroupContent = (
  content: Message['content'],
  message: Message
): content is CreatedGroupMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'created_group';
};

export const isMovedGroupThreadToGroupContent = (
  content: Message['content'],
  message: Message
): content is MovedGroupThreadToGroupMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'group_to_group';
};

export const isMovedDirectMessageToGroupContent = (
  content: Message['content'],
  message: Message
): content is MovedDirectMessageToGroupMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'pm_to_group';
};

export const isRemovedParticipantContent = (
  content: Message['content'],
  message: Message
): content is RemovedParticipantMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'removed_participant';
};

export const isJoinedNetworkContent = (
  content: Message['content'],
  message: Message
): content is JoinedNetworkSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'network_welcome';
};

export const isDisabledForeignNetworksContent = (
  content: Message['content'],
  message: Message
): content is DisabledForeignNetworksMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'local_network_policy_changed';
};

export const isRemovedForeignNetworkContent = (
  content: Message['content'],
  message: Message
): content is RemovedForeignNetworkSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'foreign_network_policy_changed';
};

export const isClosedThreadContent = (
  content: Message['content'],
  message: Message
): content is ClosedThreadSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'thread_closed';
};

export const isReopenedThreadContent = (
  content: Message['content'],
  message: Message
): content is ReopenedThreadSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'thread_reopened';
};

export const isClosedPollVotingContent = (
  content: Message['content'],
  message: Message
): content is ClosedPollVotingSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'poll_closed';
};

export const isReopenedPollVotingContent = (
  content: Message['content'],
  message: Message
): content is ReopenedPollVotingSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'poll_reopened';
};

export const isCreatedNetworkContent = (
  content: Message['content'],
  message: Message
): content is CreatedNetworkSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'created_network';
};

export const isMarkedAsQuestionContent = (
  content: Message['content'],
  message: Message
): content is MarkedMessageAsQuestionSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'changed_to_question';
};

export const isUnmarkedAsQuestionContent = (
  content: Message['content'],
  message: Message
): content is UnmarkedMessageAsQuestionSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === 'changed_to_message';
};

type GetTitleFromMessageMessageContent = (content: Message['content']) => string | undefined;
export const getTitleFromMessageContent: GetTitleFromMessageMessageContent = (content) =>
  isNormalMessageContent(content) ? content.title : undefined;

export type MessageContent =
  | NormalMessageContent
  | PollMessageContent
  | PraiseMessageContent
  | SystemMessageContent
  | MovedGroupThreadToGroupMessageContent;

export type MessageReactionsCounts = Record<ReactionType, number>;

export type MessageFeaturedReactions = Record<ReactionType, User['id'][]>;

export interface Message extends Entity {
  readonly viewerMutationId: string;
  readonly telemetryId: string;
  readonly threadId: Thread['id'];
  readonly announcementAudience?: StorylineAnnouncementAudience;
  readonly announcementAudienceIds?: string[];
  readonly announcementNotificationTargets?: StorylineAnnouncementNotificationTarget[];
  readonly announcementNotificationTargetEntityReferences?: AnnouncementNotificationTargetEntityReference[];
  readonly sender: ActorReference;
  readonly senderBadges?: MessageSenderBadge[];
  readonly featuredReactionUserIds: User['id'][];
  readonly featuredUserIdsByReaction: MessageFeaturedReactions;
  readonly featuredQuestionUpvoteUserIds: User['id'][];
  readonly upvotesTotalCount: number;
  readonly reactionsTotalCount: number;
  readonly reactionsCounts: MessageReactionsCounts;
  readonly viewerHasLiked: boolean;
  readonly viewerHasUpvoted: boolean;
  readonly viewerReaction?: ReactionType;
  readonly subtype?: MessageSubtype;
  readonly content: MessageContent;
  readonly isEdited: boolean;
  readonly isEditable: boolean;
  readonly isDeleted: boolean;
  readonly isDeletable: boolean;
  readonly isDraft: boolean;
  readonly scheduledPublishAt?: number;
  readonly isQuestion: boolean;
  readonly isArticle: boolean;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly attachments: AttachmentReference[];
  readonly attachmentsTotalCount: number;
  readonly sharedMessageId?: SharedMessage['id'];
  readonly hasDeletedSharedMessage: boolean;
  readonly threadLevel?: ThreadMessageLevel;
  readonly parentMessageId?: Message['id'];
  readonly hasFailedFileDeletions: boolean;
  readonly viewerCanShare: boolean;
  readonly availableTranslations: string[];
}

export type FeaturedReactionFields =
  | 'id'
  | 'reactionsTotalCount'
  | 'reactionsCounts'
  | 'featuredReactionUserIds'
  | 'viewerHasLiked'
  | 'viewerReaction';

export type FeaturedMessageReactions = Pick<Message, FeaturedReactionFields>;

export type Messages = EntityMap<Message>;

export type MessageEdge = EntityEdge<Message>;

export interface ReplyEdge {
  readonly cursor?: string;
  readonly nodeId: Message['id'];
  readonly senderId: User['id'];
  readonly sortKey: string;
  readonly viewerHasSeen: boolean;
}

export interface RecommendedToplevelReplyEdge {
  readonly nodeId: Message['id'];
  readonly viewerHasSeen: boolean;
}
